import { Link } from "react-router-dom";
import SEOHead, { generateWebPageSchema } from "@/components/SEOHead";

export default function PrivacyPolicy() {
  const seoData = {
    title: "Privacy Policy | Paradise Borrowed",
    description: "Learn how Paradise Borrowed collects, uses, and protects your personal information. Our privacy policy covers data collection, storage, sharing practices, and your rights.",
    url: "https://www.paradiseborrowed.com/privacy-policy"
  };

  return (
    <>
      <SEOHead
        title={seoData.title}
        description={seoData.description}
        canonicalUrl={seoData.url}
        type="website"
        schema={generateWebPageSchema(seoData)}
      />
      <div className="max-w-4xl mx-auto px-4 py-12" data-testid="privacy-page">
      <h1 className="font-heading font-bold text-3xl sm:text-4xl text-stone-900 mb-2">Privacy Policy</h1>
      <p className="text-stone-600 text-lg mb-8">
        Paradise Borrowed, Inc. — Effective Date: March 15, 2026
      </p>
      
      <div className="prose prose-stone max-w-none space-y-8">
        <p className="text-stone-600 leading-relaxed">
          This Privacy Policy describes how Paradise Borrowed, Inc. ("Paradise Borrowed," "we," "us," or "our") collects, uses, stores, and shares information about you when you access or use the Paradise Borrowed platform (the "Platform"). By using the Platform, you consent to the practices described in this Policy.
        </p>

        {/* Section 1 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">1. Information We Collect</h2>
          
          <h3 className="font-semibold text-lg text-stone-800 mt-4 mb-2">1.1 Information You Provide Directly</h3>
          <p className="text-stone-600 leading-relaxed mb-3">
            When you register or use the Platform, we may collect:
          </p>
          <ul className="list-disc pl-6 text-stone-600 space-y-2">
            <li>Full name, email address, phone number, and mailing address</li>
            <li>Profile photo and account preferences</li>
            <li>Email address (verified during account registration)</li>
            <li>Item listings, including descriptions, photographs, and condition ratings</li>
            <li>Communications sent through the Platform's messaging system</li>
            <li>Reviews and ratings you submit</li>
            <li>Dispute submissions and supporting evidence</li>
          </ul>

          <h3 className="font-semibold text-lg text-stone-800 mt-4 mb-2">1.2 Information Collected Automatically</h3>
          <p className="text-stone-600 leading-relaxed mb-3">
            When you use the Platform, we automatically collect:
          </p>
          <ul className="list-disc pl-6 text-stone-600 space-y-2">
            <li>Device type, operating system, and browser information</li>
            <li>IP address and approximate location</li>
            <li>Pages viewed, features used, and actions taken on the Platform</li>
            <li>Transaction history and booking records</li>
            <li>Cookies and similar tracking technologies (see Section 6)</li>
          </ul>

          <h3 className="font-semibold text-lg text-stone-800 mt-4 mb-2">1.3 Information from Third Parties</h3>
          <p className="text-stone-600 leading-relaxed mb-3">
            We may receive information about you from:
          </p>
          <ul className="list-disc pl-6 text-stone-600 space-y-2">
            <li>Our payment processor (Stripe) — transaction confirmation and limited payment data</li>
            <li>Email verification status and account activity</li>
            <li>Other Users — reviews, dispute filings, and reports submitted about you</li>
          </ul>
        </section>

        {/* Section 2 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">2. How We Use Your Information</h2>
          <p className="text-stone-600 leading-relaxed mb-3">
            We use the information we collect to:
          </p>
          <ul className="list-disc pl-6 text-stone-600 space-y-2">
            <li>Create and manage your account</li>
            <li>Facilitate transactions between Lenders and Borrowers</li>
            <li>Process payments and manage transactions</li>
            <li>Verify your email address and manage your account</li>
            <li>Communicate with you about bookings, transactions, and account activity</li>
            <li>Operate our dispute resolution and Trust & Safety processes</li>
            <li>Display reviews and ratings on your public profile</li>
            <li>Send you platform updates, policy changes, and promotional communications (where you have opted in)</li>
            <li>Detect, investigate, and prevent fraud, abuse, and violations of our Terms</li>
            <li>Comply with applicable legal obligations</li>
            <li>Improve the Platform's features, performance, and user experience</li>
          </ul>
        </section>

        {/* Section 3 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">3. How We Share Your Information</h2>
          
          <h3 className="font-semibold text-lg text-stone-800 mt-4 mb-2">3.1 With Other Users</h3>
          <p className="text-stone-600 leading-relaxed">
            Certain information is visible to other Users as part of the Platform's normal operation, including your display name, profile photo, verified status, item listings, and reviews. When you enter into a transaction, the counterparty User may receive your contact details and location information as necessary to complete the booking.
          </p>

          <h3 className="font-semibold text-lg text-stone-800 mt-4 mb-2">3.2 With Service Providers</h3>
          <p className="text-stone-600 leading-relaxed mb-3">
            We share information with trusted third-party service providers who assist us in operating the Platform, including:
          </p>
          <ul className="list-disc pl-6 text-stone-600 space-y-2">
            <li>Payment processor: Stripe (for transaction processing)</li>
            <li>Customer support and communications tools</li>
            <li>Cloud hosting and infrastructure providers</li>
            <li>Customer support and communications tools</li>
          </ul>
          <p className="text-stone-600 leading-relaxed mt-3">
            All service providers are contractually obligated to use your information only as directed by us and in accordance with applicable law.
          </p>

          <h3 className="font-semibold text-lg text-stone-800 mt-4 mb-2">3.3 For Legal and Safety Purposes</h3>
          <p className="text-stone-600 leading-relaxed mb-3">
            We may disclose your information if we believe in good faith that disclosure is necessary to:
          </p>
          <ul className="list-disc pl-6 text-stone-600 space-y-2">
            <li>Comply with a legal obligation, court order, or valid government request</li>
            <li>Protect the rights, property, or safety of Paradise Borrowed, our Users, or the public</li>
            <li>Detect, investigate, or prevent fraud, abuse, or violations of our Terms</li>
            <li>Report suspected criminal activity, including theft or destruction of property, to law enforcement</li>
          </ul>

          <h3 className="font-semibold text-lg text-stone-800 mt-4 mb-2">3.4 Business Transfers</h3>
          <p className="text-stone-600 leading-relaxed">
            If Paradise Borrowed is involved in a merger, acquisition, asset sale, or similar transaction, your information may be transferred as part of that transaction. We will notify you via email or in-app notice before your information is transferred and becomes subject to a different privacy policy.
          </p>

          <h3 className="font-semibold text-lg text-stone-800 mt-4 mb-2">3.5 No Sale of Personal Data</h3>
          <p className="text-stone-600 leading-relaxed">
            Paradise Borrowed does not sell, rent, or trade your personal information to third parties for their own marketing purposes.
          </p>
        </section>

        {/* Section 4 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">4. Data Retention</h2>
          <p className="text-stone-600 leading-relaxed">
            We retain your personal information for as long as your account is active or as needed to provide Platform services. We also retain information as necessary to comply with legal obligations, resolve disputes, and enforce our agreements. When you close your account, we will delete or anonymize your personal data within a reasonable period, except where retention is required by law or legitimate business necessity (such as transaction records and dispute history).
          </p>
        </section>

        {/* Section 5 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">5. Your Rights & Choices</h2>
          <p className="text-stone-600 leading-relaxed mb-3">
            Depending on your location, you may have the following rights with respect to your personal information:
          </p>
          <ul className="list-disc pl-6 text-stone-600 space-y-2">
            <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
            <li><strong>Deletion:</strong> Request deletion of your personal data, subject to legal and contractual limitations</li>
            <li><strong>Portability:</strong> Request a machine-readable copy of your data</li>
            <li><strong>Objection:</strong> Object to certain processing activities, such as direct marketing</li>
            <li><strong>Withdrawal of Consent:</strong> Where processing is based on consent, withdraw that consent at any time</li>
          </ul>
          <p className="text-stone-600 leading-relaxed mt-3">
            To exercise any of these rights, please contact us at <a href="mailto:info@paradiseborrowed.com" className="text-coral hover:underline">info@paradiseborrowed.com</a>. We will respond to verified requests within 30 days. We may require email verification before processing certain requests.
          </p>
        </section>

        {/* Section 6 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">6. Cookies & Tracking Technologies</h2>
          <p className="text-stone-600 leading-relaxed mb-3">
            We use cookies and similar technologies (such as pixel tags and local storage) to operate the Platform, remember your preferences, analyze usage patterns, and deliver relevant content. You can control cookie settings through your browser; however, disabling certain cookies may affect Platform functionality.
          </p>
          <p className="text-stone-600 leading-relaxed">
            We do not use third-party advertising cookies or share your data with advertising networks for targeted advertising purposes.
          </p>
        </section>

        {/* Section 7 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">7. Data Security</h2>
          <p className="text-stone-600 leading-relaxed">
            We implement industry-standard technical and organizational measures to protect your personal information against unauthorized access, loss, alteration, or disclosure. These measures include encryption of data in transit and at rest, access controls, and regular security assessments. However, no method of transmission over the internet or electronic storage is 100% secure. You use the Platform at your own risk, and we cannot guarantee absolute security.
          </p>
        </section>

        {/* Section 8 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">8. Children's Privacy</h2>
          <p className="text-stone-600 leading-relaxed">
            The Platform is not directed at individuals under the age of 18. We do not knowingly collect personal information from minors. If we become aware that we have collected information from a person under 18, we will promptly delete that information and terminate the associated account.
          </p>
        </section>

        {/* Section 9 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">9. Geographic Scope</h2>
          <p className="text-stone-600 leading-relaxed">
            The Platform is operated from the United States and is intended for Users in supported US states and Caribbean territories. By using the Platform, you acknowledge that your information may be processed and stored in the United States, where data protection laws may differ from those in your home jurisdiction.
          </p>
        </section>

        {/* Section 10 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">10. Third-Party Links</h2>
          <p className="text-stone-600 leading-relaxed">
            The Platform may contain links to third-party websites or services. This Privacy Policy applies only to Paradise Borrowed. We are not responsible for the privacy practices of third-party sites and encourage you to review their policies independently.
          </p>
        </section>

        {/* Section 11 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">11. Changes to This Policy</h2>
          <p className="text-stone-600 leading-relaxed">
            We may update this Privacy Policy from time to time. Material changes will be communicated to registered Users via email or in-app notification. The "Effective Date" at the top of this Policy reflects the date of the most recent revision. Your continued use of the Platform after a policy update constitutes your acceptance of the revised Policy.
          </p>
        </section>

        {/* Section 12 */}
        <section>
          <h2 className="font-heading font-semibold text-xl text-stone-900 mb-3">12. Contact</h2>
          <p className="text-stone-600 leading-relaxed mb-3">
            For privacy-related inquiries or to exercise your data rights:
          </p>
          <div className="text-stone-600 leading-relaxed">
            <p className="font-semibold">Paradise Borrowed, Inc.</p>
            <p>Email: <a href="mailto:info@paradiseborrowed.com" className="text-coral hover:underline">info@paradiseborrowed.com</a></p>
            <p>Website: <a href="https://www.paradiseborrowed.com" className="text-coral hover:underline">www.paradiseborrowed.com</a></p>
          </div>
        </section>
      </div>

      <div className="mt-12 pt-8 border-t border-stone-200">
        <Link to="/terms-of-service" className="text-coral hover:underline">
          View Terms of Service →
        </Link>
      </div>
      </div>
    </>
  );
}
